import { formatPriceRange, uploadTransformationUrl } from '@dmi-mch/utils'
import { cloudinary, crmCodes } from '@constants'
import { dimensions } from '@constants/catalog'

import { useModuleContext } from '../contexts/module'
import { useEffect } from 'react'
import useVideos from './useVideos'

const getBackgroundColorByRoomType = (type) => {
  switch (type) {
    case 'WHITE':
    default:
      return 'var(--bs-mch-white)'
    case 'DARK_GREY':
      return 'var(--bs-mch-gray-400)'
    case 'BLACK':
      return 'var(--bs-mch-black)'
    case 'LIGHT_GREY':
    case 'GREY':
      return 'var(--bs-mch-gray-100)'
  }
}

function useArtworkInformation(pageData: ABTypes.Monolith.ArtworkDetail) {
  const { dispatch, state: { CMSProps, onlineShowData, galleryData,
    accountData, labels, artworkInformation } } = useModuleContext()
  useVideos()
  /** All data **/
  const artworkInfo = pageData
  /** Information data **/
  const onlineShowName = onlineShowData?.title
  const onlineMobileShowName = onlineShowData?.mobileTitle
  const onlineShowId = onlineShowData?.id
  const showRecommendations = !onlineShowData?.hideArtworkRecommendations
  const hybridShow = onlineShowData?.hybridShow
  const id = artworkInfo.id
  const eventId = artworkInfo.eventId ? artworkInfo.eventId : null
  const name = artworkInfo.displayName
  const year = artworkInfo.year
  const endYear = artworkInfo.endYear
  const galleryId = artworkInfo.galleryId
  const gallery = artworkInfo.galleryName
  const gallerySlug = artworkInfo.galleryCleanTitle
  const artists = artworkInfo.sponsorArtists
  const artist = artworkInfo.artistName
  const artistId = artists?.map(({ artistId: aId }) => aId).join(',')
  const artistSlug = artworkInfo.artistCleanTitle
  const description = artworkInfo.description
  const price = artworkInfo.priceInUsd
  const priceInSecondCurrency = artworkInfo.priceInSecondCurrency
  const fromPrice = artworkInfo.fromPrice
  const toPrice = artworkInfo.toPrice
  const priceRange = fromPrice || toPrice ? formatPriceRange(fromPrice, toPrice) : null
  const secondCurrency = artworkInfo.secondCurrency
  const images = artworkInfo.images
  const height = artworkInfo.height
  const weight = artworkInfo.weightInKilogram ? `${artworkInfo.weightInKilogram} kg` : null
  const width = artworkInfo.width
  const depth = artworkInfo.depth
  const artworkCleanTitle = artworkInfo.cleanTitle
  const framedDepth = artworkInfo.framedDepth
  const framedHeight = artworkInfo.framedHeight
  const framedUnitMeasure = artworkInfo.framedUnitMeasure
  const framedWidth = artworkInfo.framedWidth
  const volumeInCubicMetre = artworkInfo.volumeInCubicMetre ? `${artworkInfo.volumeInCubicMetre} m³` : null
  const durationInMinute = artworkInfo.durationInMinute ? `${artworkInfo.durationInMinute}m` : null
  const firstImageUrl =
    artworkInfo?.images?.length && artworkInfo.images[0] && artworkInfo.images[0].url ? artworkInfo.images[0].url : null
  const numberOfImages = artworkInfo?.images?.length
  const galleryImage = galleryData ? galleryData.logoImageUrl : ''
  const accountName = accountData?.name
  const isGallery = accountData?.type === crmCodes.galleryAccount
  const medium = artworkInfo.medium ? artworkInfo.medium.name : null
  const material = artworkInfo.materials
  const edition = artworkInfo.edition ? artworkInfo.edition : null
  const showInRoom = artworkInfo.showInRoom ? artworkInfo.showInRoom : null
  const showEnded = artworkInfo.publicAfterEndedEvent
  const roomTitle = artworkInfo['eventDataAssociated'].cleanTitle
  const unitMeasure = artworkInfo.unitMeasure ? artworkInfo.unitMeasure : null
  const backgroundColor = getBackgroundColorByRoomType(artworkInfo.backgroundColor) ?? 'var(--bs-mch-white)'
  const reservedArtwork = artworkInfo.reservedArtwork
  /** Conditions **/
  const isPriceVisible = artworkInfo.visiblePrice
  const isSold = artworkInfo.sold
  const isPressPermitted = artworkInfo.permittedPress
  // @ts-ignore
  const hasMoreThanOneImage = artworkInfo?.images?.length > 1 ||
    // @ts-ignore
    (artworkInfo.images.length === 1 && artworkInfo.videoId)
  const artwoorkInquiryEnabled = artworkInfo.inquiryEnabled
  const showWhenCatalog = CMSProps.contact_catalog_artwork === 'true' && artwoorkInquiryEnabled
  const showWhenExhibition = CMSProps.contact_exhib_artwork === 'true' && artwoorkInquiryEnabled
  const showWhenShowroom = CMSProps.contact_show_artwork === 'true' && artwoorkInquiryEnabled
  const showMeasures =
    unitMeasure && [dimensions.cm, dimensions.inches].includes(unitMeasure.toUpperCase()) && height && width
  const showPrice = price === 0 || priceInSecondCurrency === 0
  /** Labels **/
  const labelExcludedTaxes = labels?.ArtworkUploader?.costDisclaimer
  const labelNotAvailable = labels?.ArtworkUploader?.notAvailableOptionLabel
  const labelMin = CMSProps.det_grp1_minute_label
  const labelCm = CMSProps.det_grp1_label_size_cm
  const labelInch = CMSProps.det_grp1_label_size_in
  const labelKg = CMSProps.det_grp1_kilogram_label
  const labelM3 = CMSProps.det_grp1_cubic_metre_label
  const artworkSizeLabel = labels?.ShowRoomArtwork?.artworkSizeLabel
  const artworkSizeWithFrameLabel = labels?.ShowRoomArtwork?.artworkSizeWithFrameLabel
  const seeOnWallVisible =
    (artworkInfo.roomType === 'STANDARD' || artworkInfo.roomType === 'SMALL') && artworkInfo.customRoomType !== 'HIDE'
  const shareUrl = artworkInfo.shareUrl
  const videoPosition = artworkInfo.videoPosition
  const curator = artworkInfo.curator
  const curatorPickLabel = labels?.OnlineShowRoom?.curatorPickLabel
  const headerTheme = artworkInfo.headerTheme
  const viewAtFair = artworkInfo.viewAtFair
  const croppedHeaderImages = artworkInfo.croppedHeaderImages
  const hallAndBoothInfo = artworkInfo['eventDataAssociated'].hallAndBoothInfo
  const RoomSectors = artworkInfo['eventDataAssociated'].sectors
  const roomMainSectors = artworkInfo['eventDataAssociated'].mainSectors
  const showRecommendationAlgorithms = onlineShowData?.recommendationAlgorithms

  const artworkContactFormLabel = labels?.ArtworkContactForm

  const isDesktopView = typeof window !== 'undefined' && window.innerWidth > 1023

  const headerImageDesktop = isDesktopView ?
    croppedHeaderImages && croppedHeaderImages[0] : croppedHeaderImages && croppedHeaderImages[2]
  const headerImage = croppedHeaderImages && croppedHeaderImages.length > 0 ? headerImageDesktop : images && images[0]

  const reducedHeight = headerImage?.height && Math.round(headerImage.height / 2)
  const reducedWidth = headerImage?.width && Math.round(headerImage.width / 2)
  const cl_params = `w_${reducedWidth},h_${reducedHeight},c_fill,q_auto,dpr_1.0,f_auto`
  let imageSrc = ''
  // No quality is needed in these, because the only purpose is to calculate "isPortrait TRUE/"
  if (croppedHeaderImages && croppedHeaderImages.length > 0) {
    const cloudfrontURL = `https://${getEnvConfig.fe.cdn.cloudfrontKey}.cloudfront.net${getEnvConfig.fe.cdn.cloudinaryMappingPath}${getEnvConfig.fe.cloudinary.name}/image/fetch/`
    // @ts-ignore
    const transformations = `c_fill,q_auto,dpr_1.0,f_auto,w_${parseInt(headerImage?.metadata.cropWidth / 2.5)},h_${parseInt(headerImage?.metadata.cropHeight / 2.5)}/`
    const originalImage = headerImage?.url
    imageSrc = `${cloudfrontURL}${transformations}${originalImage}`
  } else {
    imageSrc = uploadTransformationUrl(headerImage?.url ?? '', cl_params, {
      transformUrl: cloudinary.throughCloudfront
    })
  }

  useEffect(() => {
    if (artworkInformation?.id !== pageData.id && accountData && galleryData) {
      const img = new Image()
      img.src = imageSrc
      img.onload = () => {
        dispatch({
          type: 'SET_ARTWORKINFORMATION',
          payload: {
            // Alphabetical order
            accountName,
            artist,
            artistId,
            artists,
            artistSlug,
            artworkCleanTitle,
            artworkContactFormLabel,
            artworkInfo,
            artworkSizeLabel,
            artworkSizeWithFrameLabel,
            backgroundColor,
            croppedHeaderImages,
            curator,
            curatorPickLabel,
            depth,
            description,
            durationInMinute,
            edition,
            endYear,
            eventId,
            firstImageUrl,
            framedDepth,
            framedHeight,
            framedUnitMeasure,
            framedWidth,
            gallery,
            galleryId,
            galleryImage,
            gallerySlug,
            hallAndBoothInfo,
            hasMoreThanOneImage,
            headerTheme,
            height,
            hybridShow,
            id,
            images,
            imageSrc,
            isGallery,
            isPortrait: img.width < img?.height ? true : false,
            isPressPermitted,
            isPriceVisible,
            isSold,
            labelCm,
            labelExcludedTaxes,
            labelInch,
            labelKg,
            labelM3,
            labelMin,
            labelNotAvailable,
            material,
            medium,
            name,
            numberOfImages,
            onlineMobileShowName,
            onlineShowId,
            onlineShowName,
            price,
            priceInSecondCurrency,
            priceRange,
            reservedArtwork,
            roomMainSectors,
            RoomSectors,
            roomTitle,
            secondCurrency,
            seeOnWallVisible,
            shareUrl,
            showEnded,
            showInRoom,
            showMeasures,
            showPrice,
            showRecommendationAlgorithms,
            showRecommendations,
            showWhenCatalog,
            showWhenExhibition,
            showWhenShowroom,
            textColorClass: headerTheme === 'LIGHT' ? 'color-black' : 'text-white',
            unitMeasure,
            videoPosition,
            viewAtFair,
            volumeInCubicMetre,
            weight,
            width,
            year
          }
        })
      }
    }
  }, [RoomSectors, accountData, accountName, artist, artistId, artistSlug, artists,
    artworkCleanTitle, artworkContactFormLabel, artworkInfo, artworkInformation,
    artworkSizeLabel, artworkSizeWithFrameLabel, backgroundColor, croppedHeaderImages,
    curator, curatorPickLabel, depth, description, dispatch, durationInMinute, edition,
    endYear, eventId, firstImageUrl, framedDepth, framedHeight, framedUnitMeasure,
    framedWidth, gallery, galleryData, galleryId, galleryImage, gallerySlug,
    hallAndBoothInfo, hasMoreThanOneImage, headerTheme, height, hybridShow, id,
    images, isGallery, isPressPermitted, isPriceVisible, isSold, labelCm, labelExcludedTaxes,
    labelInch, labelKg, labelM3, labelMin, labelNotAvailable, material, medium, name,
    numberOfImages, onlineMobileShowName, onlineShowData, onlineShowId, onlineShowName,
    price, priceInSecondCurrency, priceRange, reservedArtwork, roomMainSectors, roomTitle,
    secondCurrency, seeOnWallVisible, shareUrl, showEnded, showInRoom, showMeasures,
    showPrice, showRecommendationAlgorithms, showRecommendations, showWhenCatalog,
    showWhenExhibition, showWhenShowroom, unitMeasure, videoPosition, viewAtFair, imageSrc,
    volumeInCubicMetre, weight, width, year, pageData])
}

export default useArtworkInformation
